import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Autocomplete,
  InputAdornment,
  CircularProgress,
  Grid,
  Typography,
  Paper,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery
} from "@mui/material";
import { ReactComponent as BarcodeScannerIcon } from "../../assets/barcode.svg";
import { ReactComponent as EditIcon } from "../../assets/edit.svg";
import { ReactComponent as SearchIcon } from "../../assets/search.svg";
import { debounce, result } from "lodash";
import BarcodeScannerOnButtonClick from "./BarcodeReader";
import useThemeColor from "../../utils/useTheme";
import {
  generateAccessToken,
  getSearchResultsForMealItems,
  gettingAllCustomFoodOptions,
  updateSearchResults
} from "../../features/level3/level3Slice";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAccessToken,
  selectCustomFoodOptions,
  selectSearchResults,
  selectSearchResultsLoading,
  selectUserFoodResults
} from "../../features/level3/level3Selectors";
import { AccessTime, Add, Clear, TrendingUp } from "@mui/icons-material";
import { useCurrentUser } from "./UserContext";
import ServicingRecipeModal from "../Modals/ServicingRecipeModal";
import dayjs from "dayjs";
import { addMealOrRecipeToDiary } from "../../api/diaryApi";
import { getCurrentUserDiaries } from "../../features/diary/diarySlice";
import { useTheme } from "@emotion/react";
import { addNotificationUser } from "../../features/auth/authSlice";
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`food-search-tabpanel-${index}`}
      aria-labelledby={`food-search-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const getTabValue = (tabValue) => {
  switch (tabValue) {
    case 0:
      return "all";
    case 1:
      return "myfoods";
    case 2:
      return "mymeals";
    case 3:
      return "myrecipes";
    case 4:
      return "quickadds";
    default:
      return "all";
  }
};

const FoodSearch = ({
  favoriteFood,
  setFavoriteFood,
  setSelectedOptionData,
  selectedOptionData,
  selectedCustomFood,
  handleResetAmount,
  handleSetAmount,
  setSelectedCustomFood,
  activeDay,
  levelId,
  type = "",
  isAddingWeight,
  setFormData,
  setIsAddingWeight,
  setIsRefreshNeeded,
  setIsMealOrRecipeTab,
  setEditCustomFoodToDiary,
  setEditMealToDiary,
  onCloseMealRecipeCreation,
  setEditRecipeToDiary,
  isBarcodeNeeded = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [accessToken, setAccessToken] = useState("");
  const [openServicingRecipeModal, setOpenServicingRecipeModal] =
    useState(false);
  const [openScanner, setOpenScanner] = useState(false);
  const existingToken = useSelector(selectAccessToken);
  const searchedData = useSelector(selectSearchResults);
  const userFoodResults = useSelector(selectUserFoodResults);
  const allCustomFoodOptions = useSelector(selectCustomFoodOptions);
  const searchLoading = useSelector(selectSearchResultsLoading);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [filterType, setFilterType] = useState(localStorage.getItem("foodSearchFilterType") || 'mostfrequent');
  const [tabValue, setTabValue] = useState(0);
  const currentUser = useCurrentUser();
  // Initialize authentication on component mount
  useEffect(() => {
    if (selectedCustomFood) {
      setSearchQuery("");
    }
  }, [selectedCustomFood]);
  useEffect(() => {
    if (existingToken) {
      setAccessToken(existingToken);
    } else {
      dispatch(generateAccessToken()).then(({ payload }) => {
        setAccessToken(payload.access_token);
      });
    }
    dispatch(updateSearchResults({ results: [] }));
    dispatch(gettingAllCustomFoodOptions());
  }, []);
  useEffect(() => {
    if (!searchQuery && !filterType) {
      setFilterType("mostfrequent");
    }
  }, [searchQuery]);
  useEffect(() => {
    if (favoriteFood) {
      setSearchQuery(favoriteFood);
      searchFood(favoriteFood);
      // setOpen(true);
    }
  }, [favoriteFood]);

  useEffect(() => {
    const data = searchedData?.map((item) => {
      return {
        name: `${item.description}${
          item.brand_name ? ` (${item.brand_name})` : ""
        } `,
        id: item.serving.serving_id,
        ...item
      };
    });
    setOptions(data);
  }, [searchedData]);
  useEffect(() => {
    dispatch(
      getSearchResultsForMealItems({
        // searchTerm: searchQuery,
        token: accessToken,
        userId: currentUser?.userId,
        currentTab: getTabValue(tabValue),
        filterType,
        type
      })
    );
  }, [dispatch, accessToken, tabValue, filterType, type]);
  // Debounced search function
  const searchFood = useCallback(
    debounce(async (query) => {
      if (query.length < 1) {
        setOptions([]);
        return;
      }
      setFilterType(null);

      try {
        await dispatch(
          getSearchResultsForMealItems({
            searchTerm: query,
            token: accessToken,
            userId: currentUser?.userId,
            currentTab: getTabValue(tabValue),
            // filterType,
            type
          })
        );
      } catch (err) {
        handleError("Failed to fetch food data");
      }
    }, 1000),
    [dispatch, accessToken, tabValue, filterType, type]
  );

  const handleInputChange = (event, newValue) => {
    setSearchQuery(newValue);
    searchFood(newValue);
    if (selectedCustomFood) setSelectedCustomFood(null);
  };

  const handleAddToDiary = async (item) => {
    // if (type === "custom food") return;
    handleResetAmount();
    if (item.datatype === "all") {
      setSelectedOptionData(
        item?.details?.extraDetails || item?.details?.item?.extraDetails
      );
      if (item?.details?.type === "customFood") {
        // setSelectedCustomFood(item?.details);
        const data = {
          ...item,
          details: {
            ...(item?.details
              ? {
                  ...item?.details,
                  extraDetails: {
                    ...item?.details?.extraDetails,
                    isCustomFood: true
                  }
                }
              : {
                  ...item?.details?.item,
                  extraDetails: {
                    ...item?.details?.item?.extraDetails,
                    isCustomFood: true
                  }
                })
          }
        };
        setSelectedOptionData(
          data?.details?.extraDetails || data?.details?.item?.extraDetails
        );
      }
      // if (
      //   item?.details?.extraDetails?.isCustomFood ||
      //   item?.details?.item?.extraDetails?.isCustomFood
      // )
      //   setSelectedCustomFood(item?.details?.item?.extraDetails);
      handleSetAmount(
        item?.details?.name,
        Math.round(item?.details?.quantity),
        Math.round(item?.details?.noOfQty),
        item?.details?.unit,
        item?.details?.type
      );
    } else if (item.datatype === "recipe") setOpenServicingRecipeModal(item);
    else if (["mycustomfood", "quickadds"].includes(item.datatype)) {
      setSelectedCustomFood(item?.customFood ? item?.customFood : item);
      const data = {
        ...(item?.customFood ? item?.customFood : item),
        serving: (item?.customFood ? item?.customFood : item)?.details?.serving,
        isCustomFood: true
      };
      setSelectedOptionData(data);
    } else {
      try {
        const tomorrow = dayjs().add(1, "day").startOf("day");

        // Check if the input date is tomorrow
        const isTomorrow = dayjs(activeDay).isSame(tomorrow, "day");

        await addMealOrRecipeToDiary({
          levelId,
          type: "meal",
          mealOrRecipeId: item._id,
          currentDayDate: activeDay,
          completionNotNeeded: isTomorrow,
          time: dayjs().format("hh:mm A")
        });
        dispatch(addNotificationUser(dayjs().format("YYYY-MM-DD")));
        setFormData({ quantity: "", [isAddingWeight]: "", customFoodType: "" });
        setIsAddingWeight("");
        setIsRefreshNeeded(true);
        dispatch(getCurrentUserDiaries({ levelId }));
      } catch (err) {
        setSnackbarMessage(err?.error || "Error while adding to diary");
        setOpenSnackbar(true);
      }
    }
  };

  const handleError = (err) => {
    console.error(err);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 2 || newValue === 3) {
      setIsMealOrRecipeTab?.(true);
    } else {
      setIsMealOrRecipeTab?.(false);
    }
    handleResetAmount();
    setSelectedOptionData(null);
    setFilterType("mostfrequent");
    setSearchQuery("");
  };
  useEffect(() => {
    if (tabValue) setFilterType("");
    setSelectedCustomFood(null);
    setSelectedOptionData(null);
  }, [tabValue]);
  const styles = useThemeColor();
  return (
    <Box>
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarMessage.includes("Success") ? "success" : "error"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {/* Search Bar */}
      {!tabValue ? (
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            placeholder="Search food..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              searchFood(e.target.value);
              if (selectedOptionData) setSelectedOptionData(null);
              if (selectedCustomFood) setSelectedCustomFood(null);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ height: "17px", color: "#4B5563" }} />
                </InputAdornment>
              )
            }}
            sx={{
              "& .MuiInputBase-root": {
                padding: "14px 11px",
                height: "100%",
                borderRadius: 2,
                color: styles.color,
                "& fieldset": {
                  borderColor: "#358ad0 !important"
                }
              },
              "& .Mui-focused": {
                "& fieldset": {
                  borderColor: "#358ad0 !important"
                }
              },
              height: "48px",
              fontSize: "14px",
              background: styles.background,
              borderRadius: 2
            }}
          />
          {/* {isBarcodeNeeded ? ( */}
          <IconButton
            onClick={() => setOpenScanner(true)}
            sx={{
              color: styles.color,
              border: `1px solid ${styles.color}`,
              borderRadius: "6px",
              padding: "10px",
              textTransform: "none",
              svg: {
                path: {
                  fill: styles.color
                }
              }
            }}
          >
            <BarcodeScannerIcon />
          </IconButton>
          <BarcodeScannerOnButtonClick
            open={openScanner}
            setOpen={setOpenScanner}
            setOpenSnackbar={setOpenSnackbar}
            setSnackbarMessage={setSnackbarMessage}
            setFavoriteFood={setFavoriteFood}
            setOpenMenu={handleOpen}
            setSearchQuery={setSearchQuery}
            setSelectedOptionData={setSelectedOptionData}
          />
        </Box>
      ) : (
        <></>
      )}
      {/* <Box sx={{ borderBottom: 1, borderColor: "divider" }}> */}
      <Tabs
        variant="scrollable"
        scrollButtons="auto"
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          "& .MuiTabs-indicator": {
            backgroundColor: "#358ad0"
          },
          "& .MuiTab-root": {
            padding: isMobile ? "4px" : "12px",
            color: styles.color,
            "&.Mui-selected": {
              color: "#358ad0"
            },
            fontSize: "14px",
            minWidth: "unset",
            textTransform: "none",
            maxWidth: isMobile ? "80px" : "120px", // Adjust based on screen size
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }
        }}
      >
        <Tab label="Lookup" />
        <Tab label="MyFoods" />
        {type ? <Tab label="MyMeals" /> : <></>}
        {type ? <Tab label="MyRecipes" /> : <></>}
        <Tab label="QuickAdds" />
      </Tabs>
      {tabValue ? (
        <></>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: isMobile ? "center" : "flex-end",
            mt: isMobile ? 0.8 : 2
          }}
        >
          <ToggleButtonGroup
            value={filterType}
            exclusive
            onChange={(event, newFilter) => {
              if (newFilter !== null) {
                setFilterType(newFilter);
                setSearchQuery("");
                localStorage.setItem("foodSearchFilterType", newFilter);
              }
            }}
            sx={{
              "& .MuiToggleButton-root": {
                fontSize: isMobile ? "0.75rem" : "0.875rem", // Smaller text for mobile
                padding: isMobile ? "4px 8px" : "6px 12px", // Adjust padding for size
                minWidth: isMobile ? "80px" : "100px", // Smaller buttons
                color: styles.color,
                textTransform: "none",
                border: "1px solid #358ad0",
                "&.Mui-selected": {
                  backgroundColor: "#358ad0",
                  color: "#fff",
                  ":hover": {
                    backgroundColor: "#358ad0"
                  }
                }
              }
            }}
          >
            <ToggleButton
              value="mostfrequent"
              sx={{
                borderRadius: "20px 0 0 20px"
              }}
            >
              <TrendingUp fontSize={"small"} sx={{ mr: 0.5 }} /> Most Frequent
            </ToggleButton>
            <ToggleButton
              value="mostrecent"
              sx={{
                borderRadius: "0 20px 20px 0"
              }}
            >
              <AccessTime fontSize={"small"} sx={{ mr: 0.5 }} /> Most Recent
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      {/* ) : (
        <></>
      )} */}

      <TabPanel value={tabValue} index={0}>
        {userFoodResults?.length ? (
          <Box sx={{ py: { xs: 0, md: 2 } }}>
            <Typography
              variant="subtitle2"
              sx={{ fontSize: "16px", fontWeight: 600 }}
            >
              History
            </Typography>
            <Paper
              sx={{
                mt: 1,
                // maxHeight: { xs: 270, md: 420 },
                overflow: "auto",
                boxShadow: "none",
                background: styles.background
              }}
            >
              {searchLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{
                    boxShadow: "none"
                    // mt: 2
                  }}
                >
                  {userFoodResults?.map((option) => (
                    <Grid item xs={12} sm={6} md={6} key={option._id}>
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        sx={{
                          // my: 2,
                          borderRadius: 1,
                          cursor: "pointer",
                          backgroundColor: styles.lightGreyColor,
                          padding: 2,
                          color: styles.color,
                          "&:hover": {
                            backgroundColor: styles.hoverColor
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                          {option.name}{" "}
                          <span style={{ fontWeight: 700, color: "#64AEDD" }}>
                            (
                            {option?.details?.noOfQty
                              ? `${Math.round(option?.details?.noOfQty)} pcs`
                              : `${Math.round(option?.details?.quantity)} ${
                                  option?.details?.unit
                                }`}
                            )
                          </span>
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "14px" }}
                        ></Typography>
                        <IconButton
                          onClick={async () =>
                            await handleAddToDiary({
                              ...option,
                              datatype: "all"
                            })
                          }
                          sx={{
                            background: styles.primaryColor,
                            color: "#FFF",
                            height: "17px",
                            width: "17px",
                            borderRadius: 1,
                            padding: 2,
                            ":hover": {
                              background: "#64AEDD",
                              color: "#FFF"
                            }
                          }}
                          // disabled
                        >
                          <Add color={styles.textColorDiary} fontSize="10px" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Box>
        ) : (
          <></>
        )}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        {selectedCustomFood ? (
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={2}
            borderRadius="8px"
            padding={"12px"}
            sx={{ background: styles.lightGreyColor }}
          >
            <Typography>{selectedCustomFood?.name}</Typography>
            <IconButton
              sx={{ color: styles.modLabel }}
              onClick={() => {
                setSelectedOptionData(null);
                setSelectedCustomFood(null);
                setFavoriteFood("");
              }}
            >
              <EditIcon height={22} width={22} />
            </IconButton>
          </Box>
        ) : userFoodResults?.length ? (
          <Box sx={{ py: { xs: 0, md: 2 } }}>
            {tabValue ? (
              <></>
            ) : (
              <Typography
                variant="subtitle2"
                sx={{ fontSize: "16px", fontWeight: 600 }}
              >
                History
              </Typography>
            )}
            <Paper
              sx={{
                mt: 1,
                // maxHeight: { xs: 270, md: 420 },
                overflow: "auto",
                boxShadow: "none",
                background: styles.background
              }}
            >
              {searchLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{
                    boxShadow: "none"
                    // mt: 2
                  }}
                >
                  {userFoodResults?.map((option) => (
                    <Grid item xs={12} sm={6} md={6} key={option._id}>
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        sx={{
                          // my: 2,
                          borderRadius: 1,
                          cursor: "pointer",
                          backgroundColor: styles.lightGreyColor,
                          padding: 2,
                          color: styles.color,
                          "&:hover": {
                            backgroundColor: styles.hoverColor
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                          {option.name || option.mealName}
                        </Typography>
                        <IconButton
                          onClick={async () =>
                            await handleAddToDiary({
                              ...option,
                              datatype: "mycustomfood"
                            })
                          }
                          sx={{
                            background: styles.primaryColor,
                            color: "#FFF",
                            height: "17px",
                            width: "17px",
                            borderRadius: 1,
                            padding: 2,
                            ":hover": {
                              background: "#64AEDD",
                              color: "#FFF"
                            }
                          }}
                          // disabled
                        >
                          <Add color={styles.textColorDiary} fontSize="10px" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Box>
        ) : (
          <Button
            onClick={() => {
              if (onCloseMealRecipeCreation) {
                onCloseMealRecipeCreation?.();
              }
              setIsAddingWeight("");
              setEditCustomFoodToDiary(true);
            }}
            type="no-outlined"
            sx={{
              width: "100%",
              textTransform: "none",
              pl: -1,
              color: styles.color,
              fontWeight: "bold",
              fontStyle: "italic",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            🍽 No my foods available. Add one now!{" "}
          </Button>
        )}
      </TabPanel>
      {!type ? (
        <></>
      ) : (
        <>
          <TabPanel value={tabValue} index={2}>
            {selectedCustomFood ? (
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                gap={2}
                borderRadius="8px"
                padding={"12px"}
                sx={{ background: styles.lightGreyColor }}
              >
                <Typography>{selectedCustomFood?.name}</Typography>
                <IconButton
                  sx={{ color: styles.modLabel }}
                  onClick={() => {
                    setSelectedOptionData(null);
                    setSelectedCustomFood(null);
                    setFavoriteFood("");
                  }}
                >
                  <EditIcon height={22} width={22} />
                </IconButton>
              </Box>
            ) : userFoodResults?.length ? (
              <Box sx={{ py: { xs: 0, md: 2 } }}>
                {tabValue ? (
                  <></>
                ) : (
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: "16px", fontWeight: 600 }}
                  >
                    History
                  </Typography>
                )}
                <Paper
                  sx={{
                    mt: 1,
                    // maxHeight: { xs: 270, md: 420 },
                    overflow: "auto",
                    boxShadow: "none",
                    background: styles.background
                  }}
                >
                  {searchLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 2 }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  ) : (
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        boxShadow: "none"
                        // mt: 2
                      }}
                    >
                      {userFoodResults?.map((option) => (
                        <Grid item xs={12} sm={6} md={6} key={option._id}>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                            sx={{
                              // my: 2,
                              borderRadius: 1,
                              cursor: "pointer",
                              backgroundColor: styles.lightGreyColor,
                              padding: 2,
                              color: styles.color,
                              "&:hover": {
                                backgroundColor: styles.hoverColor
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "14px" }}
                            >
                              {option.name || option.mealName}
                            </Typography>
                            <IconButton
                              onClick={async () =>
                                await handleAddToDiary({
                                  ...option,
                                  datatype: "meal"
                                })
                              }
                              sx={{
                                background: styles.primaryColor,
                                color: "#FFF",
                                height: "17px",
                                width: "17px",
                                borderRadius: 1,
                                padding: 2,
                                ":hover": {
                                  background: "#64AEDD",
                                  color: "#FFF"
                                }
                              }}
                              // disabled
                            >
                              <Add
                                color={styles.textColorDiary}
                                fontSize="10px"
                              />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Paper>
              </Box>
            ) : (
              <Button
                onClick={() => {
                  setIsAddingWeight("");
                  setEditMealToDiary(true);
                }}
                type="no-outlined"
                sx={{
                  width: "100%",
                  textTransform: "none",
                  pl: -1,
                  color: styles.color,
                  fontWeight: "bold",
                  fontStyle: "italic",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                🍽 No my meals available. Add one now!{" "}
              </Button>
            )}
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            {userFoodResults?.length ? (
              <Box sx={{ py: { xs: 0, md: 2 } }}>
                {tabValue ? (
                  <></>
                ) : (
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: "16px", fontWeight: 600 }}
                  >
                    History
                  </Typography>
                )}
                <Paper
                  sx={{
                    mt: 1,
                    // maxHeight: { xs: 270, md: 420 },
                    overflow: "auto",
                    boxShadow: "none",
                    background: styles.background
                  }}
                >
                  {searchLoading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 2 }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  ) : (
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        boxShadow: "none"
                        // mt: 2
                      }}
                    >
                      {userFoodResults?.map((option) => (
                        <Grid item xs={12} sm={6} md={6} key={option._id}>
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                            // onClick={() => {
                            //   setSelectedOptionData(option);
                            //   setFavoriteFood(null);
                            //   setSelectedCustomFood(null);
                            // }}
                            sx={{
                              // my: 2,
                              borderRadius: 1,
                              cursor: "pointer",
                              backgroundColor: styles.lightGreyColor,
                              padding: 2,
                              color: styles.color,
                              "&:hover": {
                                backgroundColor: styles.hoverColor
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "14px" }}
                            >
                              {option.name || option.recipeName}
                            </Typography>
                            <IconButton
                              onClick={async () =>
                                await handleAddToDiary({
                                  ...option,
                                  datatype: "recipe"
                                })
                              }
                              sx={{
                                background: styles.primaryColor,
                                color: "#FFF",
                                height: "17px",
                                width: "17px",
                                borderRadius: 1,
                                padding: 2,
                                ":hover": {
                                  background: "#64AEDD",
                                  color: "#FFF"
                                }
                              }}
                              // disabled
                            >
                              <Add
                                color={styles.textColorDiary}
                                fontSize="10px"
                              />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Paper>
              </Box>
            ) : (
              <Button
                onClick={() => {
                  setIsAddingWeight("");
                  setEditRecipeToDiary(true);
                }}
                type="no-outlined"
                sx={{
                  width: "100%",
                  textTransform: "none",
                  pl: -1,
                  color: styles.color,
                  fontWeight: "bold",
                  fontStyle: "italic",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                🍽 No my recipes available. Add one now!{" "}
              </Button>
            )}
          </TabPanel>
        </>
      )}
      <TabPanel value={tabValue} index={4}>
        {userFoodResults?.length ? (
          <Box sx={{ py: { xs: 0, md: 2 } }}>
            {tabValue ? (
              <></>
            ) : (
              <Typography
                variant="subtitle2"
                sx={{ fontSize: "16px", fontWeight: 600 }}
              >
                History
              </Typography>
            )}
            <Paper
              sx={{
                mt: 1,
                // maxHeight: { xs: 270, md: 420 },
                overflow: "auto",
                boxShadow: "none",
                background: styles.background
              }}
            >
              {searchLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{
                    boxShadow: "none"
                    // mt: 2
                  }}
                >
                  {userFoodResults?.map((option) => (
                    <Grid item xs={12} sm={6} md={6} key={option._id}>
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        // onClick={() => {
                        //   setSelectedOptionData(option);
                        //   setFavoriteFood(null);
                        //   setSelectedCustomFood(null);
                        // }}
                        sx={{
                          // my: 2,
                          borderRadius: 1,
                          cursor: "pointer",
                          backgroundColor: styles.lightGreyColor,
                          padding: 2,
                          color: styles.color,
                          "&:hover": {
                            backgroundColor: styles.hoverColor
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                          {option.name || option.recipeName}
                        </Typography>
                        <IconButton
                          onClick={async () =>
                            await handleAddToDiary({
                              ...option,
                              datatype: "quickadds"
                            })
                          }
                          sx={{
                            background: styles.primaryColor,
                            color: "#FFF",
                            height: "17px",
                            width: "17px",
                            borderRadius: 1,
                            padding: 2,
                            ":hover": {
                              background: "#64AEDD",
                              color: "#FFF"
                            }
                          }}
                          // disabled
                        >
                          <Add color={styles.textColorDiary} fontSize="10px" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          </Box>
        ) : (
          <Typography
            sx={{
              width: "100%",
              textTransform: "none",
              pl: -1,
              color: styles.color,
              fontWeight: "bold",
              fontStyle: "italic",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            ⏳ Nothing here yet! Wait for the admin to add items.
          </Typography>
        )}
      </TabPanel>

      {searchQuery.length >= 2 && options?.length ? (
        <Box
          sx={{
            ...(userFoodResults?.length && {
              py: 2
            })
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontSize: "16px", fontWeight: 600 }}
          >
            Suggestions
          </Typography>
          <Paper
            sx={{
              mt: 1,
              // maxHeight: { xs: 270, md: 420 },
              overflow: "auto",
              boxShadow: "none",
              background: styles.background
            }}
          >
            {searchLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Grid
                container
                spacing={2}
                sx={{
                  boxShadow: "none"
                  // mt: 2
                }}
              >
                {selectedOptionData?.name ? (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={6}
                    key={selectedOptionData?._id}
                  >
                    <Box
                      onClick={() => {
                        handleResetAmount();
                        setSelectedOptionData(null);
                      }}
                      sx={{
                        // my: 2,
                        borderRadius: 1,
                        background: styles.primaryColor,
                        // ? styles.primaryColor
                        // : styles.lightGreyColor,
                        cursor: "pointer",
                        // backgroundColor: styles.lightGreyColor,
                        padding: 2,
                        color: styles.color,
                        "&:hover": {
                          backgroundColor: styles.hoverColor
                        }
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "14px" }}>
                        {selectedOptionData?.name}
                      </Typography>
                    </Box>
                  </Grid>
                ) : (
                  options.map((option) => (
                    <Grid item xs={12} sm={6} md={6} key={option.id}>
                      <Box
                        onClick={() => {
                          handleResetAmount();
                          if (selectedOptionData?.name === option?.name) {
                            setSelectedOptionData(null);
                          } else {
                            setSelectedOptionData(option);
                            setFavoriteFood(null);
                            setSelectedCustomFood(null);
                          }
                        }}
                        sx={{
                          // my: 2,
                          borderRadius: 1,
                          background:
                            selectedOptionData?.name === option?.name
                              ? styles.primaryColor
                              : styles.lightGreyColor,
                          // ? styles.primaryColor
                          // : styles.lightGreyColor,
                          cursor: "pointer",
                          // backgroundColor: styles.lightGreyColor,
                          padding: 2,
                          color: styles.color,
                          "&:hover": {
                            backgroundColor: styles.hoverColor
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: "14px" }}>
                          {option.name}
                        </Typography>
                      </Box>
                    </Grid>
                  ))
                )}
              </Grid>
            )}
          </Paper>
        </Box>
      ) : (
        <></>
      )}
      {/* {(type
        ? allCustomFoodOptions?.filter((item) => item?.type === type)
        : allCustomFoodOptions
      )?.length ? (
        <Box sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontSize: "15px" }}>
            Custom Foods
          </Typography>
          <Grid container spacing={2}>
            {(type
              ? allCustomFoodOptions?.filter((item) => item?.type === type)
              : allCustomFoodOptions
            )?.map((item) => (
              <Grid key={item?._id} item xs={12} sm={6}>
                <Paper
                  elevation={selectedCustomFood?._id === item?._id ? 3 : 1}
                  sx={{
                    background: styles.lightGreyColor,
                    color: styles.color,
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      elevation: 3,
                      borderColor: "#358ad0"
                    },
                    display: "flex",
                    gap: "10px",
                    justifyContent: "center",
                    textTransform: "none"
                  }}
                  onClick={() => {
                    if (!selectedCustomFood) {
                      handleResetAmount();
                      setSelectedCustomFood(item);
                      const data = {
                        ...item,
                        serving: item.details.serving,
                        isCustomFood: true
                      };
                      setSelectedOptionData(data);
                      setSearchQuery("");
                      setFavoriteFood(null);
                    } else {
                      setSelectedCustomFood(null);
                      // setSelectedOptionData(null);
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "center",
                      px: 2,
                      py: 1,
                      color:
                        selectedCustomFood?._id === item?._id
                          ? "#358ad0"
                          : styles.color,
                      fontSize:
                        selectedCustomFood?._id === item?._id ? "16px" : "13px"
                    }}
                  >
                    {item.name}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <></>
      )} */}
      <ServicingRecipeModal
        open={openServicingRecipeModal}
        setOpen={setOpenServicingRecipeModal}
        handleSubmit={async (e, amount) => {
          e.preventDefault();
          e.stopPropagation();
          const tomorrow = dayjs().add(1, "day").startOf("day");

          // Check if the input date is tomorrow
          let isTomorrow = "";
          if (activeDay) isTomorrow = dayjs(activeDay).isSame(tomorrow, "day");
          try {
            await addMealOrRecipeToDiary({
              levelId,
              type: "recipe",
              mealOrRecipeId: openServicingRecipeModal._id,
              currentDayDate: activeDay,
              time: dayjs().format("hh:mm A"),
              totalConsumed: amount,
              completionNotNeeded: isTomorrow ? isTomorrow : false
            });
            dispatch(addNotificationUser(dayjs().format("YYYY-MM-DD")));
            setOpenServicingRecipeModal(null);
            setFormData({
              quantity: "",
              [isAddingWeight]: "",
              customFoodType: ""
            });
            setIsAddingWeight("");
            setIsRefreshNeeded(true);
            setTimeout(() => {
              dispatch(
                getCurrentUserDiaries({
                  levelId
                })
              );
            }, 2000);
          } catch (err) {
            setSnackbarMessage(err?.error || "Error while adding to diary");
            setOpenSnackbar(true);
          }
        }}
      />
    </Box>
  );
};

export default FoodSearch;
